import { motion } from 'framer-motion'
import { PROJECTS, LINKS } from '../config'

const GithubIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.699-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.268 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.026 2.747-1.026.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
  </svg>
)

const ExternalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

function ProjectImages({ p }) {
  if (p.images) {
    return (
      <div className="mb-6 grid grid-cols-2 gap-4">
        {p.images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${p.title} preview ${i + 1}`}
            loading="lazy"
            decoding="async"
            className="h-40 w-full border-4 border-[var(--proj-ink)] object-cover object-top md:h-52"
          />
        ))}
      </div>
    )
  }
  if (p.image) {
    return (
      <img
        src={p.image}
        alt={`${p.title} preview`}
        loading="lazy"
        decoding="async"
        className="mb-6 h-48 w-full border-4 border-[var(--proj-ink)] object-cover object-top md:h-64"
      />
    )
  }
  return null
}

function ProjectCard({ p, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: (index % 2) * 0.06 }}
      data-testid={`project-card-${index + 1}`}
      className={`group border-4 border-[var(--proj-ink)] bg-white p-6 shadow-[6px_6px_0_var(--proj-ink)] transition-all hover:-translate-y-1 hover:shadow-[9px_9px_0_var(--proj-ink)] md:p-8 ${
        p.big ? 'md:col-span-2' : ''
      }`}
    >
      <ProjectImages p={p} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-mono inline-block bg-[var(--proj-ink)] px-2.5 py-1 text-[11px] font-bold text-[var(--proj-yellow)]">
          {p.tag}
        </span>
        <div className="flex gap-2">
          {p.github && (
            <a
              href={p.github}
              target="_blank"
              rel="noreferrer"
              data-testid={`project-github-${index + 1}`}
              aria-label={`${p.title} source code`}
              className="rounded-full bg-[var(--proj-ink)]/5 p-1.5 transition-colors hover:bg-[var(--proj-ink)]/10"
            >
              <GithubIcon />
            </a>
          )}
          {p.link && (
            <a
              href={p.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`project-live-${index + 1}`}
              aria-label={`${p.title} live site`}
              className="rounded-full bg-[var(--proj-ink)]/5 p-1.5 transition-colors hover:bg-[var(--proj-ink)]/10"
            >
              <ExternalIcon />
            </a>
          )}
        </div>
      </div>
      <h3 className="font-impact mt-5 text-2xl leading-[0.95] md:text-4xl">{p.title}</h3>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-black/75 md:text-base">{p.desc}</p>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {p.stack.map((s) => (
          <span
            key={s}
            className="font-mono border-2 border-[var(--proj-ink)] px-2 py-1 text-[11px] font-semibold"
          >
            {s}
          </span>
        ))}
        {p.secondLink && (
          <a
            href={p.secondLink.href}
            target="_blank"
            rel="noreferrer"
            data-testid={`project-second-link-${index + 1}`}
            className="font-mono border-2 border-[var(--proj-ink)] bg-[var(--proj-yellow)] px-2 py-1 text-[11px] font-bold transition hover:-translate-y-0.5"
          >
            {p.secondLink.label} ↗
          </a>
        )}
        {p.linkLabel && (
          <span className="font-mono text-[11px] font-semibold text-black/50">{p.linkLabel}</span>
        )}
      </div>
    </motion.div>
  )
}

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative bg-[var(--proj-bg)] px-4 py-24 text-[var(--proj-ink)] md:px-8 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b-4 border-[var(--proj-ink)] pb-6">
          <h2 className="font-impact text-4xl leading-[0.9] md:text-7xl">Things I have built.</h2>
          <span className="font-mono text-xs">// {PROJECTS.length} SHIPPED — MORE IN PROGRESS</span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.title} p={p} index={i} />
          ))}
        </div>

        <a
          href={LINKS.github}
          target="_blank"
          rel="noreferrer"
          data-testid="projects-view-all-github"
          className="font-impact mt-10 inline-flex items-center gap-3 border-4 border-[var(--proj-ink)] bg-[var(--proj-yellow)] px-6 py-4 text-lg shadow-[6px_6px_0_var(--proj-ink)] transition-all hover:-translate-y-1 hover:shadow-[9px_9px_0_var(--proj-ink)]"
        >
          VIEW ALL ON GITHUB →
        </a>
      </div>
    </section>
  )
}
